// web/src/screens/admin/textsets_edit.ts
//
// Named TextSet editor: short_code (immutable — read-only) + name fields + an
// ordered members editor (mountTextSetMembersEditor). Members are loaded from
// GET /api/admin/textsets/{id}/members and persisted as a full replacement via
// the PATCH `members` field. Renaming a TextSet requires DELETE + a new POST
// (short_code is the natural key), so the short_code field is read-only here.
//
// Resolution: the TextSet identifier on this URL is short_code; the FE finds
// the row via list-and-find since /api/admin/textsets has no by-short-code
// resolver (no I-07 entry for TextSet).
//
// All API-supplied text via textContent / .value (the admin XSS lock).

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import { adminGet, adminPatch, adminDelete, AdminError } from "../../api/admin.js";
import { closeOpenTextPopup } from "./popup_stack.js";
import { mountTextSetMembersEditor, type TextSetMembersEditorHandle } from "./textset_editor.js";
import { mountUsedByPanel } from "./used_by_panel.js";
import type {
  AdminTextSetDTO, AdminTextSetMemberDTO, AdminRouteProps,
} from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function field(parent: HTMLElement, label: string, control: HTMLElement): void {
  const wrap = document.createElement("label");
  wrap.setAttribute(
    "style",
    "display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151;",
  );
  const lab = document.createElement("span");
  lab.textContent = label;
  lab.setAttribute("style", "font-weight: 500;");
  wrap.append(lab, control);
  parent.appendChild(wrap);
}

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = props as AdminRouteProps | undefined;
  const sc = p && p.kind === "admin-textset-edit" ? p.textSetShortCode : "";

  const main = renderAdminShell(root, [
    { label: "TextSets", href: "/admin/textsets" },
    { label: sc || "?" },
  ]);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const form = document.createElement("form");
  form.setAttribute(
    "style",
    "display: none; flex-direction: column; gap: 12px; max-width: 720px; background: white; padding: 16px; border-radius: 6px; border: 1px solid #e5e7eb;",
  );
  main.appendChild(form);

  const errorBox = document.createElement("div");
  errorBox.setAttribute(
    "style",
    "margin-top: 12px; padding: 12px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; display: none; font-size: 13px; color: #991b1b;",
  );
  main.appendChild(errorBox);

  const shortCodeInput = document.createElement("input");
  shortCodeInput.type = "text";
  shortCodeInput.readOnly = true;
  shortCodeInput.title = "short_code is immutable — rename via Delete + new TextSet";
  shortCodeInput.setAttribute(
    "style",
    "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: #f3f4f6; color: #6b7280;",
  );
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.setAttribute(
    "style",
    "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px;",
  );
  field(form, "Short code (immutable)", shortCodeInput);
  field(form, "Name", nameInput);

  // Members editor — host populated in load() once the TextSet id is known.
  const membersHost = document.createElement("div");
  form.appendChild(membersHost);

  // Buttons row.
  const headerBtns = document.createElement("div");
  headerBtns.setAttribute("style", "display: flex; gap: 8px;");
  const saveBtn = document.createElement("button");
  saveBtn.type = "submit";
  saveBtn.textContent = "Save";
  saveBtn.setAttribute(
    "style",
    "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;",
  );
  const deleteBtn = document.createElement("button");
  deleteBtn.type = "button";
  deleteBtn.textContent = "Delete";
  deleteBtn.setAttribute(
    "style",
    "padding: 8px 14px; background: #dc2626; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px; margin-left: auto;",
  );
  headerBtns.append(saveBtn, deleteBtn);
  form.appendChild(headerBtns);

  let current: AdminTextSetDTO | null = null;
  let editor: TextSetMembersEditorHandle | null = null;
  let unmountUsedBy: (() => void) | null = null;

  async function load(): Promise<void> {
    try {
      // No by-short-code resolver for TextSet — list-and-find.
      const allSets = await adminGet<AdminTextSetDTO[]>("/api/admin/textsets");
      const found = allSets.find((x) => x.short_code === sc);
      if (!found || !found.id) {
        status.textContent = `TextSet “${sc}” not found.`;
        return;
      }
      current = found;
      shortCodeInput.value = found.short_code ?? "";
      nameInput.value = found.name ?? "";
      editor = mountTextSetMembersEditor(membersHost, { title: "Members" });
      try {
        const members = await adminGet<AdminTextSetMemberDTO[]>(
          `/api/admin/textsets/${encodeURIComponent(found.id)}/members`,
        );
        editor.setMembers(members);
      } catch (err) {
        editor.setMembers([]);
        errorBox.textContent = err instanceof AdminError
          ? `Could not load members (${err.status}): ${err.bodyText.slice(0, 200)}`
          : `Could not load members: ${(err as Error).message}`;
        errorBox.style.display = "block";
      }
      status.style.display = "none";
      form.style.display = "flex";
      unmountUsedBy?.();
      unmountUsedBy = mountUsedByPanel(main, { kind: "textset", id: found.id });
    } catch (err) {
      if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
      } else {
        status.textContent = `Failed to load: ${(err as Error).message}`;
      }
    }
  }

  async function onSave(e: Event): Promise<void> {
    e.preventDefault();
    if (!current || !current.id || !editor) return;
    let members: AdminTextSetMemberDTO[];
    try {
      members = await editor.flush();
    } catch (err) {
      errorBox.textContent = (err as Error).message;
      errorBox.style.display = "block";
      return;
    }
    const patch: Partial<AdminTextSetDTO> = { members };
    if (nameInput.value !== (current.name ?? "")) patch.name = nameInput.value;
    try {
      const updated = await adminPatch<Partial<AdminTextSetDTO>, AdminTextSetDTO>(
        `/api/admin/textsets/${encodeURIComponent(current.id)}`,
        patch,
      );
      current = updated;
      nameInput.value = updated.name ?? "";
      showAdminToast(document.body, "Saved");
      errorBox.style.display = "none";
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Save failed (${err.status}): ${err.bodyText.slice(0, 300)}`
        : `Save failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  async function onDelete(): Promise<void> {
    if (!current || !current.id) return;
    if (!window.confirm(`Delete TextSet ${current.short_code}? This wipes its members and cannot be undone.`)) return;
    try {
      await adminDelete(`/api/admin/textsets/${encodeURIComponent(current.id)}`);
      showAdminToast(document.body, "Deleted");
      navigateAdmin("/admin/textsets");
    } catch (err) {
      const msg = err instanceof AdminError
        ? `Delete failed (${err.status}): ${err.bodyText.slice(0, 200)}`
        : `Delete failed: ${(err as Error).message}`;
      errorBox.textContent = msg;
      errorBox.style.display = "block";
    }
  }

  form.addEventListener("submit", onSave);
  deleteBtn.addEventListener("click", onDelete);

  void load();

  return () => {
    form.removeEventListener("submit", onSave);
    deleteBtn.removeEventListener("click", onDelete);
    editor?.unmount();
    unmountUsedBy?.();
    closeOpenTextPopup();
  };
}
